import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ListOrdered, Plus, Rocket, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Input } from "@/components/ui";
import { api, ApiError, canManage, type OrderReservation, type ReservationSettings } from "@/lib/api";
import { fmtNumber } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
const STRATEGIES: { value: ReservationSettings["strategy"]; title: string; text: string }[] = [
  { value: "fifo", title: msg("Wer zuerst bestellt, bekommt zuerst"), text: msg("Streng nach Bestelleingang. Ein älterer Auftrag hält seine Ware, auch wenn noch etwas fehlt – ein späterer Auftrag überholt ihn nie, auch nicht mit nur einer Position. Ideal, wenn erst verkauft und dann nachbestellt wird.") },
  { value: "max_orders", title: msg("Möglichst viele Pakete"), text: msg("Der Bestand wird so verteilt, dass möglichst viele Endkunden ihr komplettes Paket bekommen – kleine Aufträge mit knappen Artikeln zuerst. Große Aufträge warten dafür ggf. länger.") },
];

/**
 * Bestandsvergabe je Kunde: Reihenfolge, Kanal-Vorrang, Schutz vor Dauerwarten, Klärfälle.
 * Connect übergibt einen Auftrag erst ans Lager, wenn ihm Bestand zugeteilt ist – die Reihenfolge entscheidet nie das Lager.
 */
export function ReservationSettingsCard() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["reservation", tenantId], queryFn: () => api.orders.reservationSettings(tenantId) });
  const preview = useQuery({ queryKey: ["reservation-preview", tenantId], queryFn: () => api.orders.reservationPreview(tenantId), staleTime: 60_000 });
  const [draft, setDraft] = useState<ReservationSettings | null>(null);
  useEffect(() => {
    if (q.data && !draft) setDraft(q.data.settings);
  }, [q.data, draft]);
  const save = useMutation({
    mutationFn: (s: ReservationSettings) => api.orders.saveReservationSettings(tenantId, s),
    onSuccess: (s) => {
      toast.success("Gespeichert", { description: msg("Wartende Aufträge werden gleich nach der neuen Reihenfolge geprüft.") });
      setDraft(s);
      void qc.invalidateQueries({ queryKey: ["reservation", tenantId] });
      void qc.invalidateQueries({ queryKey: ["reservation-preview", tenantId] });
    },
    onError: (e) => toast.error("Speichern fehlgeschlagen", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  if (!q.data || !draft) return null;
  const editable = canManage(role);
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data.settings);
  const nameOf = new Map(q.data.channels.map((c) => [c.id, c.name]));
  const rest = q.data.channels.filter((c) => !draft.channelPriority.includes(c.id));
  const move = (i: number, d: -1 | 1) => {
    const xs = [...draft.channelPriority];
    [xs[i], xs[i + d]] = [xs[i + d]!, xs[i]!];
    setDraft({ ...draft, channelPriority: xs });
  };
  const p = preview.data;

  return (
    <Card className="p-5 mb-4">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><ListOrdered className="size-4 text-fg-muted" /> {t("Bestandsvergabe")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">
        {t("In welcher Reihenfolge offene Aufträge Bestand bekommen. Ein Auftrag geht erst ans Lager, wenn ihm Bestand zugeteilt ist – so entscheidet nie das Lager über die Reihenfolge.")}
      </p>

      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {STRATEGIES.map((o) => (
          <label key={o.value} className={cx("flex items-start gap-2.5 rounded-xl border p-3 transition-colors", draft.strategy === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60", editable ? "cursor-pointer" : "cursor-default")}>
            <input type="radio" className="mt-1 accent-ink" checked={draft.strategy === o.value} disabled={!editable} onChange={() => setDraft({ ...draft, strategy: o.value })} />
            <span>
              <span className="block text-[13px] font-medium">{t(o.title)}</span>
              <span className="block text-[12px] text-fg-muted">{t(o.text)}</span>
              {p && p.open > 0 && (
                <span className="mt-1.5 inline-flex items-center gap-1.5 text-[12px]">
                  <Badge tone={o.value === "fifo" ? "neutral" : p.maxOrders > p.fifo ? "success" : "neutral"}>
                    {t("jetzt")} {fmtNumber(o.value === "fifo" ? p.fifo : p.maxOrders)} {t("von")} {fmtNumber(p.open)} {t("Aufträgen lieferbar")}
                  </Badge>
                </span>
              )}
            </span>
          </label>
        ))}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <p className="text-[13px] font-medium">{t("Vorrang für Kanäle")}</p>
          <p className="text-[12px] text-fg-muted">{t("Aufträge dieser Kanäle bekommen zuerst Bestand (z. B. Amazon wegen strenger Versandfristen), danach alle übrigen.")}</p>
          <ol className="mt-2 space-y-1.5">
            {draft.channelPriority.map((id, i) => (
              <li key={id} className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-[13px]">
                <span className="tabular-nums text-fg-subtle w-4">{i + 1}.</span>
                <span className="flex-1 truncate">{nameOf.get(id) ?? t("Kanal")}</span>
                {editable && (
                  <>
                    <button className="size-6 grid place-items-center rounded hover:bg-surface-2 text-fg-muted disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t("Nach oben")}><ArrowUp className="size-3.5" /></button>
                    <button className="size-6 grid place-items-center rounded hover:bg-surface-2 text-fg-muted disabled:opacity-30" disabled={i === draft.channelPriority.length - 1} onClick={() => move(i, 1)} aria-label={t("Nach unten")}><ArrowDown className="size-3.5" /></button>
                    <button className="size-6 grid place-items-center rounded hover:bg-surface-2 text-fg-muted" onClick={() => setDraft({ ...draft, channelPriority: draft.channelPriority.filter((x) => x !== id) })} aria-label={t("Vorrang entfernen")}><X className="size-3.5" /></button>
                  </>
                )}
              </li>
            ))}
          </ol>
          {editable && rest.length > 0 && (
            <label className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted">
              <Plus className="size-3.5" />
              <select value="" onChange={(e) => e.target.value && setDraft({ ...draft, channelPriority: [...draft.channelPriority, e.target.value] })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px] text-fg">
                <option value="">{t("Kanal mit Vorrang hinzufügen …")}</option>
                {rest.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          {!draft.channelPriority.length && !editable && <p className="mt-2 text-[12.5px] text-fg-subtle">{t("Kein Kanal-Vorrang")}</p>}
        </div>

        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 text-[13px] font-medium">
              <input type="checkbox" className="accent-ink" disabled={!editable} checked={draft.agingDays != null} onChange={(e) => setDraft({ ...draft, agingDays: e.target.checked ? 3 : null })} />
              {t("Schutz vor Dauerwarten")}
            </label>
            <p className="text-[12px] text-fg-muted ml-6">
              {t("Aufträge, die länger warten, rücken vor jeden Kanal-Vorrang")}{draft.strategy === "max_orders" ? t(" – auch große Aufträge kommen so sicher dran") : ""}.
            </p>
            {draft.agingDays != null && (
              <div className="ml-6 mt-1.5 flex items-center gap-2 text-[13px]">
                {t("ab")}
                <Input value={String(draft.agingDays)} disabled={!editable} inputMode="numeric" className="w-16 [&_input]:text-right" onChange={(e) => setDraft({ ...draft, agingDays: clamp(e.target.value, 1) })} />
                {t("Tagen Wartezeit")}
              </div>
            )}
          </div>
          <div>
            <p className="text-[13px] font-medium">{t("Aufträge in Klärung")}</p>
            <p className="text-[12px] text-fg-muted">{t("Adresse, Zahlung oder eigene Regel: wie lange ein solcher Auftrag seine Ware behält, bevor Nachfolgende sie bekommen. Ist er geklärt, reiht er sich nach Bestelleingang wieder ein.")}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px]">
              <span className={cx(draft.clarificationDays == null && "opacity-40")}>{t("höchstens")}</span>
              <Input value={draft.clarificationDays == null ? "" : String(draft.clarificationDays)} disabled={!editable || draft.clarificationDays == null} inputMode="numeric" className="w-16 [&_input]:text-right" onChange={(e) => setDraft({ ...draft, clarificationDays: clamp(e.target.value, 0) })} />
              <span className={cx(draft.clarificationDays == null && "opacity-40")}>{t("Tage")}</span>
              <label className="ml-2 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted">
                <input type="checkbox" className="accent-ink" disabled={!editable} checked={draft.clarificationDays == null} onChange={(e) => setDraft({ ...draft, clarificationDays: e.target.checked ? null : 3 })} />
                {t("unbegrenzt")}
              </label>
            </div>
            {draft.clarificationDays === 0 && <p className="mt-1 text-[12px] text-fg-subtle">{t("0 = Aufträge in Klärung halten keine Ware.")}</p>}
          </div>
        </div>
      </div>

      {editable && (
        <div className="mt-4 flex items-center gap-2">
          <Button variant="primary" size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(draft)}>{t("Speichern")}</Button>
          {dirty && <Button variant="ghost" size="sm" onClick={() => setDraft(q.data.settings)}>{t("Verwerfen")}</Button>}
        </div>
      )}
    </Card>
  );
}

const clamp = (v: string, min: number) => Math.max(min, Math.min(90, Number(v.replace(/[^0-9]/g, "")) || min));

const KLASS_TEXT: Record<NonNullable<OrderReservation["klass"]>, string> = {
  boosted: "vorgezogen",
  aging: "Vorrang wegen langer Wartezeit",
  channel: "Kanal mit Vorrang",
  normal: "",
  released: "",
};

/** Am Auftrag mit Fehlbestand: Platz in der Bestandsvergabe, Vorziehen */
export function OrderReservationInfo({ orderId }: { orderId: string }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["order-reservation", tenantId, orderId], queryFn: () => api.orders.reservation(tenantId, orderId), refetchInterval: 60_000 });
  const boost = useMutation({
    mutationFn: (on: boolean) => api.orders.boostReservation(tenantId, orderId, on),
    onSuccess: (_r, on) => {
      toast.success(on ? "Vorgezogen" : "Vorziehen zurückgenommen", { description: on ? "Der Auftrag bekommt vor allen anderen Bestand und geht raus, sobald genug da ist." : "Der Auftrag reiht sich wieder nach den Regeln ein." });
      void qc.invalidateQueries({ queryKey: ["order-reservation", tenantId, orderId] });
      void qc.invalidateQueries({ queryKey: ["order", tenantId, orderId] });
    },
    onError: (e) => toast.error("Nicht möglich", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const r = q.data;
  if (!r) return <span className="block text-[12px] text-fg-muted">{t("Geht automatisch ans Lager, sobald Bestand für ihn da ist.")}</span>;
  const extra = r.klass ? KLASS_TEXT[r.klass] : "";
  return (
    <span className="block text-[12px] text-fg-muted">
      {r.position != null ? (
        <>
          <b className="text-fg">{t("Platz")} {fmtNumber(r.position)}</b> {t("von")} {fmtNumber(r.queued)} {t("in der Bestandsvergabe (")}{r.strategy === "fifo" ? t("wer zuerst bestellt, bekommt zuerst") : t("möglichst viele Pakete")}{extra ? ` · ${extra}` : ""}).{" "}
          {r.covered ? t("Bestand ist zugeteilt – geht gleich ans Lager.") : t("Geht automatisch ans Lager, sobald Bestand für ihn da ist.")}
        </>
      ) : (
        t("Geht automatisch ans Lager, sobald Bestand für ihn da ist.")
      )}
      <span className="mt-1.5 flex flex-wrap items-center gap-2">
        {r.boostedAt ? (
          <Button size="sm" variant="ghost" loading={boost.isPending} onClick={() => boost.mutate(false)}>{t("Vorziehen zurücknehmen")}</Button>
        ) : (
          <Button size="sm" icon={<Rocket className="size-3.5" />} loading={boost.isPending} onClick={() => boost.mutate(true)}>{t("Vorziehen")}</Button>
        )}
        <span className="text-fg-subtle">{t("oder „Trotz Fehlbestand übertragen“ – Logiful versendet dann nach Wareneingang.")}</span>
      </span>
    </span>
  );
}
